import React, { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import { PDFViewer } from '@react-pdf/renderer';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useNavigate } from 'react-router-dom';
import PlantationPDF from '../reports/PlantationPDF';
import { Link } from "react-router-dom";

const LoadingSkeleton = () => {
  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#fff',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      zIndex: 1000
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Skeleton width={120} height={40} />
        <Skeleton width={200} height={20} />
      </div>
      
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Skeleton width={200} height={30} />
      </div>
      
      <Skeleton height={2} />
      
      <div style={{ marginTop: '20px' }}>
        <Skeleton count={5} height={20} style={{ marginBottom: '10px' }} />
      </div>
      
      <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
        <Icon icon="eos-icons:loading" width="50" height="50" />
      </div>
    </div>
  );
};

const PlantationPDFComponent = ({ id }) => {
  const [plantationData, setPlantationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const APP_BASE_URL = process.env.REACT_APP_URL;

  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchPlantationData = async () => {
      try {
        if (!id) {
          throw new Error('No plantation ID provided');
        }

        const token = localStorage.getItem('token');
        if (!token) {
          setAccessDenied(true);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/plantation/pafs/get-plantation-by-id-for-pdf/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status) {
          const data = response.data.data;
          setPlantationData(data);
          const qrCodeData = `${APP_BASE_URL}/plantation/pdf/${data._id}`;
          const url = await QRCode.toDataURL(qrCodeData, { width: 100 });
          setQrCodeUrl(url);
        } else {
          if (response.data.message === 'Unauthorized') {
            setAccessDenied(true);
          } else {
            throw new Error('Invalid response status');
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setAccessDenied(true);
        } else {
          setError(error.message);
          toast.error('Failed to fetch plantation data.');
          console.error('Error fetching plantation data:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlantationData();
  }, [API_BASE_URL, id, APP_BASE_URL]);

  if (accessDenied) {
    navigate('/access-denied');
    return null;
  }

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        zIndex: 1000
      }}>
        <Icon icon="material-symbols:error-outline" width="50" height="50" color="red" />
        <h3 style={{ marginTop: '20px', color: '#dc3545' }}>Error Loading this Plantation Data!</h3>
        <h6 style={{ marginTop: '20px', color: '#dc3546bb' }}>Please contact us at gislab652@gmail.com / devteam@fwf.punjab.gov.pk</h6>
        <p style={{ textAlign: 'center', maxWidth: '500px', marginTop: '10px' }}>
          {error}
        </p>
       <Link
          to={`${APP_BASE_URL}/sign-in`}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#22c55e',
            color: 'white',
            borderRadius: '5px',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icon icon="ri:home-4-line" /> Go Back To Home
        </Link>
      </div>
    );
  }

  if (!plantationData) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
        zIndex: 1000
      }}>
        <Icon icon="mdi:file-document-alert-outline" width="50" height="50" color="#6c757d" />
        <h3 style={{ marginTop: '20px', color: '#6c757d' }}>No Plantation Data</h3>
        <p style={{ textAlign: 'center', maxWidth: '500px', marginTop: '10px' }}>
          No plantation data available for the provided ID.
        </p>
        <Link
          to="/"
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#0d6efd',
            color: 'white',
            borderRadius: '5px',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Icon icon="ri:home-4-line" /> Go Back To Home
        </Link>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      <PDFViewer width="100%" height="100%">
        <PlantationPDF plantationData={plantationData} qrCodeUrl={qrCodeUrl} />
      </PDFViewer>
    </div>
  );
};

export default PlantationPDFComponent;